import React, { useEffect } from "react";
import { useLocalObservable } from "mobx-react-lite";
import { getAuth, signOut } from "firebase/auth";
import app, { waitForAuthReady, resetAuthReady } from "../firebase";
import { refreshToken } from "../api/auth";

import {
  userDetail,
  userUpdate,
  userChangePhoto,
  login as loginApi,
  logout as logoutApi,
  changePassword as changePasswordApi,
} from "api/auth";

import { getClient } from "api/core";

export const AuthContext = React.createContext();
const ACCOUNT_KEY = "selectedAccount";
const auth = getAuth();

const AuthProvider = ({ children }) => {
  const store = useLocalObservable(() => ({
    loading: true,
    loggedIn: false,
    user: null,
    userType: "user",
    selectedAccount: null,

    get hasSelectedAccount() {
      return !!localStorage.getItem(ACCOUNT_KEY);
    },

    get isAuthenticated() {
      return store.loggedIn;
    },

    get isAdmin() {
      return store.user.scope === "admin";
    },

    setLoading: (loading) => {
      store.loading = loading;
    },

    setLoggedIn: (loggedIn) => {
      store.loggedIn = loggedIn;
    },

    setUser: (user) => {
      store.user = user;
    },

    setSelectedAccount: (client) => {
      store.selectedAccount = client;
    },

    resetSelectedAccount: () => {
      localStorage.removeItem(ACCOUNT_KEY);
      store.selectedAccount = null;
    },

    async checkAuth() {
      store.loading = true;
      try {
        await store.fetchUser();
      } catch {
        store.logout();
      } finally {
        store.loading = false;
      }
    },

    login: (payload) => {
      store.loading = true;
      return loginApi({
        email: payload.email,
        password: payload.password,
      })
        .then((res) => {
          store.loggedIn = true;
          return store.fetchUser();
        })
        .catch((err) => {
          store.reset();
          if (err.response) {
            throw err.response;
          }
        });
    },

    oauthLogin: (payload) => {
      store.user = payload.user;
      store.loggedIn = true;
      return store.fetchUser();
    },

    setTokens: (access) => {
      store.loggedIn = true;
      return store.fetchUser();
    },

    reset: () => {
      localStorage.removeItem(ACCOUNT_KEY);
      store.user = null;
      store.loggedIn = false;
      store.selectedAccount = null;
      store.loading = false;
      resetAuthReady();
    },

    logout: async () => {
      store.loading = true;
      try {
        await logoutApi();
        await signOut(auth);
        resetAuthReady();
        store.reset();
      } catch (err) {
        store.reset();
      } finally {
        store.loading = false;
      }
    },

    fetchUser: async () => {
      store.loading = true;
      try {
        const user = await waitForAuthReady();
        if (!user) {
          store.reset();
          return;
        }

        const freshToken = await user.getIdToken(true);
        await refreshToken({ accessToken: freshToken });

        const res = await userDetail();
        store.user = res.data.user;
        store.loggedIn = true;
        return res.data;
      } catch (err) {
        store.reset();
      } finally {
        store.loading = false;
      }
    },

    fetchSelectedAccount: () => {
      store.loading = true;
      let uid = localStorage.getItem(ACCOUNT_KEY);
      if (!uid) {
        store.loading = false;
        return Promise.resolve();
      }
      return getClient(uid)
        .then((res) => {
          store.selectedAccount = res.data.data;
          store.loading = false;
          return res.data;
        })
        .catch((err) => {
          store.loading = false;
          store.resetSelectedAccount();
          throw err;
        });
    },

    updateUser: (data) => {
      return userUpdate(data)
        .then((res) => {
          store.user = res.data;
          return res.data;
        })
        .catch((err) => {
          throw err;
        });
    },

    changePhoto: (data) => {
      return userChangePhoto(data)
        .then((res) => {
          store.user = res.data;
          return res.data;
        })
        .catch((err) => {
          throw err;
        });
    },

    changePassword: (data) => {
      return changePasswordApi(data)
        .then(() => {
          return true;
        })
        .catch((err) => {
          throw err;
        });
    },
  }));

  useEffect(() => {
    store.checkAuth();
  }, []);

  return <AuthContext.Provider value={store}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
